/**
 * address: the one formatter for every surface that renders a client
 * address as text. `faceSheet.ts` and the Addresses card both import
 * from here, so the whole record shows one string for one address.
 *
 * Every part is trimmed before empty parts are dropped, including
 * inside the state/ZIP join, so a whitespace-only field behaves exactly
 * like an absent one and no surface ever prints a stray comma.
 *
 * `zipProblem` is the on-blur ZIP validation shared by both address
 * forms (the phoneProblem precedent, src/lib/phone.ts): empty is always
 * fine, a value unchanged from what is already stored is always fine
 * (a legacy ZIP stays editable around, on an unrelated field), and
 * otherwise the value must pass `isValidZip`.
 *
 * `addressDuplicateKey` is the one duplicate-address key, the client twin
 * of api/_shared/address.ts (tests/ui/addressDuplicateKey.test.ts proves
 * the two agree): Edit contact runs it before sending a new address, and
 * the server refuses the same duplicates.
 *
 * `missingHomeFields` is the home-address presence rule, the client twin
 * of api/_shared/address.ts (tests/ui/homeAddressFieldsParity.test.ts
 * proves the two agree): a primary (home) address needs a street, city,
 * state and ZIP, and both address dialogs wait for all four.
 *
 * `newAddressStartState` is where a new address's State starts: the
 * tenant's first working state (getTenantConfig's working_states, in the
 * server's order), or "Choose a state" when the tenant has none. An
 * existing row always opens on its own state.
 *
 * `addressTextChanged` is the client twin of the server's
 * addressPartsChanged (api/_shared/address.ts; tests/ui/formatAddressParity.test.ts
 * proves the two agree): a save whose text changed is checked against
 * Azure Maps before it is sent.
 *
 * `isPoBoxLine` is the client twin of the server's
 * (api/_shared/geocode/geocodeMatch.ts; the same test proves the two
 * agree): a PO Box line 1 is never sent to the check, since the geocoder
 * places it at town level, approximate by design.
 */

import { isValidZip, normalizeZip, stateCodeFromText } from '@/lib/usStates';
import type { WorkingState } from '@/lib/tenantConfig';

type AddressParts = {
  line1: string;
  line2?: string;
  city?: string;
  state?: string;
  zip?: string;
};

function trimmed(s: string | undefined): string {
  return (s ?? '').trim();
}

/** The city, then the state and ZIP joined by a space, dropping any part
 *  that is empty or whitespace-only. */
export function formatCityStateZip(a: { city?: string; state?: string; zip?: string }): string {
  const stateZip = [trimmed(a.state), trimmed(a.zip)].filter(Boolean).join(' ');
  return [trimmed(a.city), stateZip].filter(Boolean).join(', ');
}

/** Line 1, line 2, then the city, state and ZIP tail, dropping any part
 *  that is empty or whitespace-only. */
export function formatAddress(a: AddressParts): string {
  return [trimmed(a.line1), trimmed(a.line2), formatCityStateZip(a)].filter(Boolean).join(', ');
}

/** A client address as one line: its place name first (0241), then the
 *  address, blanks dropped. Every surface that prints a client address on
 *  one line prints it through this. */
export function addressLine(a: AddressParts & { placeName?: string | null }): string {
  return [trimmed(a.placeName ?? undefined), formatAddress(a)].filter(Boolean).join(', ');
}

/** A place as one line (0233): its name, then its address, blanks
 *  dropped, joined by ", ". The server's twin is formatPlace in
 *  api/_shared/address.ts. */
export function formatPlace(
  name: string | null | undefined,
  parts: { line1: string | null; line2: string | null; city: string | null; state: string | null; zip: string | null } | null | undefined,
): string {
  const address =
    parts === null || parts === undefined
      ? ''
      : formatAddress({
          line1: parts.line1 ?? '',
          line2: parts.line2 ?? undefined,
          city: parts.city ?? undefined,
          state: parts.state ?? undefined,
          zip: parts.zip ?? undefined,
        });
  return [trimmed(name ?? undefined), address].filter(Boolean).join(', ');
}

const ZIP_MESSAGE = 'Enter a five-digit ZIP, or ZIP+4 (12345-6789).';

/** null when `value` trimmed is empty, equals `stored` trimmed and
 *  case-insensitively, or passes `isValidZip`; otherwise the friendly,
 *  fix-forward message. */
export function zipProblem(value: string, stored: string | null): string | null {
  const t = value.trim();
  if (t === '') return null;
  if (stored !== null && t.toLowerCase() === stored.trim().toLowerCase()) return null;
  if (isValidZip(t)) return null;
  return ZIP_MESSAGE;
}

/** The five postal fields the duplicate key reads; a missing part counts as ''. */
export type AddressKeyParts = {
  line1: string;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
};

/** Trimmed, whitespace runs collapsed to one space. */
function collapse(v: string | null | undefined): string {
  return (v ?? '').trim().replace(/\s+/g, ' ');
}

/**
 * Two addresses with the same key are the same address. Each of the five
 * fields is trimmed, has its whitespace runs collapsed to one space and
 * is lowercased. The state is first mapped to its code when it names a
 * state or territory (a state's full name and its code, in any case, key
 * as the lowercase code); any other
 * state text compares as it is written. The ZIP is first normalised, so
 * nine bare digits key the same as the hyphenated ZIP+4.
 */
export function addressDuplicateKey(a: AddressKeyParts): string {
  const state = collapse(a.state);
  const zip = a.zip === null || a.zip === undefined ? '' : normalizeZip(a.zip);
  return JSON.stringify(
    [a.line1, a.line2, a.city, stateCodeFromText(state) ?? state, zip].map((v) => collapse(v).toLowerCase()),
  );
}

/** A part a home address must carry. */
export type HomeAddressField = 'line1' | 'city' | 'state' | 'zip';

/** The fields a home address lacks, in form order: each of street, city,
 *  state and ZIP that is null, undefined or blank once trimmed. A present
 *  legacy value (a full state name, a short ZIP) is not missing. */
export function missingHomeFields(a: {
  line1: string | null | undefined;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
}): HomeAddressField[] {
  const blank = (v: string | null | undefined): boolean => (v ?? '').trim() === '';
  const out: HomeAddressField[] = [];
  if (blank(a.line1)) out.push('line1');
  if (blank(a.city)) out.push('city');
  if (blank(a.state)) out.push('state');
  if (blank(a.zip)) out.push('zip');
  return out;
}

/** The State a new address starts on: the first working state's code,
 *  or '' when the tenant has none. */
export function newAddressStartState(workingStates: readonly WorkingState[]): string {
  return workingStates[0]?.code ?? '';
}

/** The five postal fields a text change reads; a missing part counts as ''. */
export type AddressTextParts = {
  line1: string | null | undefined;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
};

/** True when any of the five postal fields differs, trimmed and case
 *  folded; the ZIP is compared normalised, so nine bare digits sent back
 *  as stored are not a change. */
export function addressTextChanged(before: AddressTextParts, after: AddressTextParts): boolean {
  const norm = (v: string | null | undefined): string => (v ?? '').trim().toLowerCase();
  const zip = (v: string | null | undefined): string => (v === null || v === undefined ? '' : normalizeZip(v));
  return (
    norm(before.line1) !== norm(after.line1) ||
    norm(before.line2) !== norm(after.line2) ||
    norm(before.city) !== norm(after.city) ||
    norm(before.state) !== norm(after.state) ||
    norm(zip(before.zip)) !== norm(zip(after.zip))
  );
}

/** The PO Box openings a line may start with, read with dots removed and
 *  whitespace collapsed, lower-cased. */
const PO_BOX_OPENING = /^(?:p\s*o\s*box|post\s*office\s*box)\b\s*/;

/** True when the line opens with a PO Box opening ("PO Box", "P.O. Box",
 *  "Post Office Box", "POBox") followed by a token holding a digit. */
export function isPoBoxLine(line: string): boolean {
  const text = line.replace(/\./g, '').toLowerCase().replace(/\s+/g, ' ').trim();
  const m = PO_BOX_OPENING.exec(text);
  if (m === null) return false;
  const first = text.slice(m[0].length).split(' ')[0] ?? '';
  return /\d/.test(first);
}

/** An address held as its five parts; a blank part is null. */
export interface PostalParts {
  line1: string | null;
  line2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
}

const POSTAL_PART_KEYS = ['line1', 'line2', 'city', 'state', 'zip'] as const;

/** The browser twin of the server's postalAddressPartsOf
 *  (api/_shared/suggestedValueText.ts): a parts object, or its JSON, as
 *  parts; anything else (a plain address string, JSON that is not parts)
 *  as null. */
export function postalPartsOf(value: unknown): PostalParts | null {
  let parsed: unknown = value;
  if (typeof value === 'string') {
    try {
      parsed = JSON.parse(value);
    } catch {
      return null;
    }
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
  const record = parsed as Record<string, unknown>;
  const keys = Object.keys(record);
  if (keys.length === 0 || !keys.every((k) => (POSTAL_PART_KEYS as readonly string[]).includes(k))) return null;
  const part = (k: (typeof POSTAL_PART_KEYS)[number]): string | null => {
    const v = record[k];
    return typeof v === 'string' && v.trim() !== '' ? v : null;
  };
  return { line1: part('line1'), line2: part('line2'), city: part('city'), state: part('state'), zip: part('zip') };
}

/** Parts as one line of text ("Town, ST" for a town with no street). */
export function formatPostalParts(p: PostalParts): string {
  return formatAddress({
    line1: p.line1 ?? '',
    line2: p.line2 ?? undefined,
    city: p.city ?? undefined,
    state: p.state ?? undefined,
    zip: p.zip ?? undefined,
  });
}
